// roomSettingsForm — Phase 6 Task 6.6-B 設定モーダルのフィールド validation
// + outcome マップ.
//
// 設計:
// - **フィールドごとに独立した form**. 「保存」ボタンも fields 単位で出して
//   1 回の API 呼び出しに対応させる. (CreateRoomPage の一括 submit とは違う)
// - validation rule:
//     - ルーム名: 0..128 字 (空欄は許容しない、PATCH 仕様に合わせる)
//     - owner password: 8..128 字 (Task 6.6-B 仕様の 8..128)
//     - email: 軽量 regex (server 側 zod `.email()` が本格 reject)
//     - isPublic: boolean のみ
// - outcome マップ: 各エンドポイント outcome union を画面側 union に潰す.
//     - 共通: 'ok' / 'unauthorized' / 'invalid_body' / 'rate_limited' / 'unknown'
//     - email のみ追加: 'partial' (ok だが emailVerificationSent=false)
//
// Why a separate module:
// 既存の `createRoomForm.ts` と同じ流儀で、DOM 引かない pure function に切り出して
// vitest node-only で網羅. RoomSettingsModal.tsx は薄い wiring に留める.
//
// Note: PATCH /admin/rooms/:id は body 両 undefined で 400 を返す仕様だが、本
// モジュールは「name 単独 patch」「isPublic 単独 patch」に閉じた callable を
// 提供するので、そのケースは発生しない (UI から両方 undefined で叩く経路がない).

import type {
  PatchRoomOutcome,
  SetOwnerEmailOutcome,
  SetOwnerPasswordOutcome,
} from '../infrastructure/dispatcher/DispatcherClient.js';
import { formatMs } from './uiFeedback.js';

/* -----------------------------------------------------------------------------
 * Constants
 * --------------------------------------------------------------------------- */

/** Server PATCH /admin/rooms/:id name は zod min(1).max(128). UI も同じ範囲. */
export const ROOM_NAME_MIN_LENGTH = 1;
export const ROOM_NAME_MAX_LENGTH = 128;

/** Phase 6 Task 6.6-B 仕様: 8..128 字 (UI 側、wire は server 256). */
export const OWNER_PASSWORD_MIN_LENGTH = 8;
export const OWNER_PASSWORD_MAX_LENGTH = 128;

/** Server `EmailSchema.max(320)` と一致. */
export const OWNER_EMAIL_MAX_LENGTH = 320;

/* -----------------------------------------------------------------------------
 * Field validation
 * --------------------------------------------------------------------------- */

export type RoomNameError = 'empty' | 'too-long';
export type OwnerPasswordError = 'too-short' | 'too-long';
export type OwnerEmailError = 'empty' | 'too-long' | 'invalid-format';

export interface RoomNameValidOk {
  readonly outcome: 'ok';
  /** Trimmed value to send. */
  readonly value: string;
}
export interface RoomNameValidFailed {
  readonly outcome: 'failed';
  readonly error: RoomNameError;
}
export type RoomNameValidation = RoomNameValidOk | RoomNameValidFailed;

export const validateRoomName = (raw: string): RoomNameValidation => {
  const trimmed = raw.trim();
  if (trimmed.length < ROOM_NAME_MIN_LENGTH) {
    return { outcome: 'failed', error: 'empty' };
  }
  if (trimmed.length > ROOM_NAME_MAX_LENGTH) {
    return { outcome: 'failed', error: 'too-long' };
  }
  return { outcome: 'ok', value: trimmed };
};

export interface OwnerPasswordValidOk {
  readonly outcome: 'ok';
  /** Raw value (no trim — passphrase 性). */
  readonly value: string;
}
export interface OwnerPasswordValidFailed {
  readonly outcome: 'failed';
  readonly error: OwnerPasswordError;
}
export type OwnerPasswordValidation = OwnerPasswordValidOk | OwnerPasswordValidFailed;

export const validateOwnerPassword = (raw: string): OwnerPasswordValidation => {
  if (raw.length < OWNER_PASSWORD_MIN_LENGTH) {
    return { outcome: 'failed', error: 'too-short' };
  }
  if (raw.length > OWNER_PASSWORD_MAX_LENGTH) {
    return { outcome: 'failed', error: 'too-long' };
  }
  return { outcome: 'ok', value: raw };
};

export interface OwnerEmailValidOk {
  readonly outcome: 'ok';
  /** Trimmed value to send. */
  readonly value: string;
}
export interface OwnerEmailValidFailed {
  readonly outcome: 'failed';
  readonly error: OwnerEmailError;
}
export type OwnerEmailValidation = OwnerEmailValidOk | OwnerEmailValidFailed;

/**
 * Lightweight email regex — server zod `.email()` is the source of truth.
 * False negative 寄り (= 通せば server が落とす). createRoomForm と同じパターン.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validateOwnerEmail = (raw: string): OwnerEmailValidation => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { outcome: 'failed', error: 'empty' };
  }
  if (trimmed.length > OWNER_EMAIL_MAX_LENGTH) {
    return { outcome: 'failed', error: 'too-long' };
  }
  if (!EMAIL_RE.test(trimmed)) {
    return { outcome: 'failed', error: 'invalid-format' };
  }
  return { outcome: 'ok', value: trimmed };
};

/**
 * isPublic は CB 値そのものなので、boolean 以外を許さない型ガード相当.
 */
export type IsPublicError = 'not-boolean';
export type IsPublicValidation =
  | { readonly outcome: 'ok'; readonly value: boolean }
  | { readonly outcome: 'failed'; readonly error: IsPublicError };

export const validateIsPublic = (raw: unknown): IsPublicValidation => {
  if (typeof raw !== 'boolean') {
    return { outcome: 'failed', error: 'not-boolean' };
  }
  return { outcome: 'ok', value: raw };
};

// entry-preview-members-2026-07-21: `entryFlagInitial` は作成フォームとの
// 共有化 (案 A) で application/entryVisibilityFields.ts へ移動した.

/* -----------------------------------------------------------------------------
 * Field error → user-facing string
 * --------------------------------------------------------------------------- */

export const roomNameErrorText = (err: RoomNameError): string => {
  switch (err) {
    case 'empty':
      return 'ルーム名を入力してください。';
    case 'too-long':
      return `ルーム名は ${ROOM_NAME_MAX_LENGTH} 文字以内で入力してください。`;
  }
};

export const ownerPasswordErrorText = (err: OwnerPasswordError): string => {
  switch (err) {
    case 'too-short':
      return `オーナーパスワードは ${OWNER_PASSWORD_MIN_LENGTH} 文字以上で入力してください。`;
    case 'too-long':
      return `オーナーパスワードは ${OWNER_PASSWORD_MAX_LENGTH} 文字以内で入力してください。`;
  }
};

export const ownerEmailErrorText = (err: OwnerEmailError): string => {
  switch (err) {
    case 'empty':
      return 'メールアドレスを入力してください。';
    case 'too-long':
      return `メールアドレスは ${OWNER_EMAIL_MAX_LENGTH} 文字以内で入力してください。`;
    case 'invalid-format':
      return 'メールアドレスの形式が正しくありません。';
  }
};

/* -----------------------------------------------------------------------------
 * Outcome → screen-side state
 *
 * 各エンドポイントの outcome union を「保存ボタンの状態」に潰す:
 *  - 'ok'             : 保存成功
 *  - 'partial'        : email 専用 (200 + emailVerificationSent=false)
 *  - 'unauthorized'   : adminToken が無効/期限切れ → ログイン誘導
 *  - 'invalid_body'   : 通常起こらない (UI 側が validate 済み) が出たら fallback
 *  - 'rate_limited'   : email でだけ起こる (5/h) — retry message 表示
 *  - 'unknown'        : network / invalid / http-status / not-found 全部
 * --------------------------------------------------------------------------- */

export type FieldSaveResult =
  | { readonly outcome: 'ok'; readonly message?: string }
  | { readonly outcome: 'partial'; readonly message: string }
  | { readonly outcome: 'unauthorized'; readonly message: string }
  | { readonly outcome: 'invalid_body'; readonly message: string }
  | { readonly outcome: 'rate_limited'; readonly message: string }
  | { readonly outcome: 'unknown'; readonly message: string };

const UNAUTHORIZED_MESSAGE = '認証に失敗しました。オーナーとしてログインし直してください。';
const INVALID_BODY_MESSAGE = 'リクエストの形式が正しくありません。入力をご確認ください。';
const UNKNOWN_MESSAGE = 'エラーが発生しました。時間を置いて再度お試しください。';

/**
 * Map `patchRoom` outcome (ルーム名 / isPublic) → save result.
 */
export const mapPatchRoomOutcome = (out: PatchRoomOutcome): FieldSaveResult => {
  switch (out.outcome) {
    case 'ok':
      return { outcome: 'ok' };
    case 'unauthorized':
      return { outcome: 'unauthorized', message: UNAUTHORIZED_MESSAGE };
    case 'not-found':
      return { outcome: 'unknown', message: 'ルームが見つかりませんでした。' };
    case 'invalid-body':
      return { outcome: 'invalid_body', message: INVALID_BODY_MESSAGE };
    case 'rate-limited':
      return {
        outcome: 'rate_limited',
        message: `操作制限に達しました (${formatMs(out.retryAfterMs)} 後に再試行してください)。`,
      };
    case 'unknown':
      return { outcome: 'unknown', message: UNKNOWN_MESSAGE };
  }
};

/**
 * Map `setOwnerPassword` outcome → save result.
 */
export const mapSetOwnerPasswordOutcome = (out: SetOwnerPasswordOutcome): FieldSaveResult => {
  switch (out.outcome) {
    case 'ok':
      return { outcome: 'ok', message: 'オーナーパスワードを設定しました。' };
    case 'unauthorized':
      return { outcome: 'unauthorized', message: UNAUTHORIZED_MESSAGE };
    case 'invalid-body':
      return { outcome: 'invalid_body', message: INVALID_BODY_MESSAGE };
    case 'rate-limited':
      return {
        outcome: 'rate_limited',
        message: `操作制限に達しました (${formatMs(out.retryAfterMs)} 後に再試行してください)。`,
      };
    case 'unknown':
      return { outcome: 'unknown', message: UNKNOWN_MESSAGE };
  }
};

/**
 * Map `setOwnerEmail` outcome → save result.
 *
 * 200 + `emailVerificationSent: false` は `partial` に格上げして「再送可能」
 * UX を出せるようにする (Phase 6 Task 6.5 確定方針 U).
 */
export const mapSetOwnerEmailOutcome = (out: SetOwnerEmailOutcome): FieldSaveResult => {
  switch (out.outcome) {
    case 'ok':
      if (out.emailVerificationSent) {
        return {
          outcome: 'ok',
          message: '確認メールを送信しました。受信トレイをご確認ください。',
        };
      }
      return {
        outcome: 'partial',
        message: 'メール送信に失敗しました。後でもう一度お試しください。',
      };
    case 'unauthorized':
      return { outcome: 'unauthorized', message: UNAUTHORIZED_MESSAGE };
    case 'invalid-body':
      return { outcome: 'invalid_body', message: INVALID_BODY_MESSAGE };
    case 'rate-limited':
      return {
        outcome: 'rate_limited',
        message: `送信回数制限に達しました (${formatMs(out.retryAfterMs)} 後に再試行してください)。`,
      };
    case 'unknown':
      return { outcome: 'unknown', message: UNKNOWN_MESSAGE };
  }
};
